import React from "react";
import PieDonutChart from "style-guide/component/PieDonutChart/PieDonutChart";
import { DashboardStatsData } from "types/application";

interface PieChartsSectionProps {
  dashboardStats: DashboardStatsData;
}

const PieChartsSection: React.FC<PieChartsSectionProps> = ({
  dashboardStats,
}) => {
  // Data for Hours Saved chart using dashboardStats
  const hoursSavedData = [
    {
      name: "Create",
      hours: Number(
        (dashboardStats.hoursSaved?.rfpGenerateHours || 0).toFixed(1)
      ),
      percent:
        dashboardStats.hoursSaved?.totalHoursSaved > 0
          ? Math.round(
              ((dashboardStats.hoursSaved?.rfpGenerateHours || 0) /
                dashboardStats.hoursSaved.totalHoursSaved) *
                100
            )
          : 0,
      color: "#69B1FF",
    },
    {
      name: "Analyze",
      hours: Number(
        (dashboardStats.hoursSaved?.rfpAnalyzeHours || 0).toFixed(1)
      ),
      percent:
        dashboardStats.hoursSaved?.totalHoursSaved > 0
          ? Math.round(
              ((dashboardStats.hoursSaved?.rfpAnalyzeHours || 0) /
                dashboardStats.hoursSaved.totalHoursSaved) *
                100
            )
          : 0,
      color: "#5CDBD3",
    },
    {
      name: "Negotiate",
      hours: Number(
        (dashboardStats.hoursSaved?.negotiateHours || 0).toFixed(1)
      ),
      percent:
        dashboardStats.hoursSaved?.totalHoursSaved > 0
          ? Math.round(
              ((dashboardStats.hoursSaved?.negotiateHours || 0) /
                dashboardStats.hoursSaved.totalHoursSaved) *
                100
            )
          : 0,
      color: "#597EF7",
    },
  ].filter((item) => item.hours > 0); // Only show categories with actual hours

  // Data for Cost Savings chart using dashboardStats
  const costSavingsData = [
    {
      name: "Strategic Savings",
      price: Number(
        (dashboardStats.costSavingData?.analyzeStrategicValue || 0).toFixed(2)
      ),
      percent:
        dashboardStats.costSavingData?.costSaving > 0
          ? Math.round(
              ((dashboardStats.costSavingData?.analyzeStrategicValue || 0) /
                dashboardStats.costSavingData.costSaving) *
                100
            )
          : 0,
      color: "#91CAFF",
    },
    {
      name: "Operational Savings",
      price: Number(
        (dashboardStats.costSavingData?.operational || 0).toFixed(2)
      ),
      percent:
        dashboardStats.costSavingData?.costSaving > 0
          ? Math.round(
              ((dashboardStats.costSavingData?.operational || 0) /
                dashboardStats.costSavingData.costSaving) *
                100
            )
          : 0,
      color: "#4096FF",
    },
    {
      name: "Negotiation Savings",
      price: Number(
        (dashboardStats.costSavingData?.negotiateStrategicValue || 0).toFixed(2)
      ),
      percent:
        dashboardStats.costSavingData?.costSaving > 0
          ? Math.round(
              ((dashboardStats.costSavingData?.negotiateStrategicValue || 0) /
                dashboardStats.costSavingData.costSaving) *
                100
            )
          : 0,
      color: "#0958D9",
    },
  ].filter((item) => item.price > 0); // Only show categories with actual savings

  // Only show charts if there's meaningful data
  const hasHoursData =
    hoursSavedData.length > 0 && dashboardStats.hoursSaved?.totalHoursSaved > 0;
  const hasCostData =
    costSavingsData.length > 0 && dashboardStats.costSavingData?.costSaving > 0;

  if (!hasHoursData && !hasCostData) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-y-6 xl:gap-x-6 mt-6">
      {hasHoursData && (
        <PieDonutChart
          data={hoursSavedData}
          totalLabel="Total"
          totalValue={`${dashboardStats.hoursSaved.totalHoursSaved?.toLocaleString()}h`}
          dataKey="hours"
          heading="Hours Saved"
        />
      )}
      {hasCostData && (
        <PieDonutChart
          data={costSavingsData}
          totalLabel="Total"
          totalValue={`$${Number(dashboardStats.costSavingData.costSaving?.toFixed(2)).toLocaleString()}`}
          dataKey="price"
          heading="Cost Savings"
        />
      )}
    </div>
  );
};

export default PieChartsSection;
